// Friendly, localized wording for agent chat errors. The raw error is kept
// as secondary detail; this only picks the headline.

export type AgentErrorContext = 'turn' | 'submit';

export type AgentErrorKey =
  | 'errTurn'
  | 'errTimeout'
  | 'errNetwork'
  | 'errSubmit'
  | 'errRejected'
  | 'errService'
  | 'errBusy'
  | 'errCredits'
  | 'errSignup'
  | 'errContentMinor'
  | 'errContentExplicit';

export type AgentErrorInfo = { raw: string; status?: number; code?: string; reason?: string };

export function agentErrorKey(context: AgentErrorContext, e: AgentErrorInfo): AgentErrorKey {
  const raw = e.raw ?? '';
  const content = contentRejection(e);
  if (content) return content === 'explicit' ? 'errContentExplicit' : 'errContentMinor';
  if (e.code === 'insufficient_credits' || /not enough credits/i.test(raw)) return 'errCredits';
  if (e.code === 'anonymous_forbidden' || /sign up to generate/i.test(raw)) return 'errSignup';
  if (e.code === 'turn_in_progress' || /turn_in_progress/.test(raw)) return 'errBusy';
  if (/timeout|timed out|\((504)\)/i.test(raw)) return 'errTimeout';
  if (/failed to fetch|networkerror|network error|load failed|fetch failed|econnreset|socket|terminated|bodystreambuffer|connection lost/i.test(raw)) {
    return 'errNetwork';
  }
  if (context === 'submit') {
    // "portal 400: …" (validation) or "Gateway submit failed (batch 422): …"
    if (/^portal 4\d\d\b|\((?:batch|api) 4\d\d\)|unprocessable/i.test(raw)) return 'errRejected';
    if (/^portal 5\d\d\b|gateway submit failed|gateway_submit_failed/i.test(raw) || (e.status ?? 0) >= 500) return 'errService';
    return 'errSubmit';
  }
  return 'errTurn';
}

// The generation service's content check refused the job. Retrying the same
// plan is refused again, so the UI hands it back to the agent to revise.
export function contentRejection(e: AgentErrorInfo): 'minor' | 'explicit' | null {
  const raw = e.raw ?? '';
  if (e.code === 'content_rejected') return e.reason === 'explicit' ? 'explicit' : 'minor';
  if (/illegal content|under-aged/i.test(raw)) return 'minor';
  if (/explicit content not allowed/i.test(raw)) return 'explicit';
  return null;
}
